import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Container,
  InputAdornment,
  IconButton,
  Link,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Visibility, VisibilityOff, Email, Lock } from '@mui/icons-material';
import { motion } from 'framer-motion';
import { Logo } from '../components/Logo';
import { MagicBackground } from '../components/MagicBackground';
import { useNavigate } from 'react-router-dom';
import { signIn, getCurrentUser, fetchAuthSession, signOut } from 'aws-amplify/auth';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const navigate = useNavigate();

  // Check if user is already authenticated on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const session = await fetchAuthSession();
        if (session.tokens?.idToken) {
          // User is already authenticated, redirect to dashboard
          navigate('/dashboard');
          return;
        }
      } catch (error) {
        // Not authenticated, continue with login form
      } finally {
        setCheckingAuth(false);
      }
    };
    checkAuth();
  }, [navigate]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      console.log('[LOGIN] Starting login process for:', email);
      
      // Check if user is already signed in
      try {
        const currentUser = await getCurrentUser();
        console.log('[LOGIN] User already signed in:', currentUser.username);
        // User is already signed in, sign them out first
        await signOut();
        console.log('[LOGIN] Signed out existing user');
      } catch (error) {
        console.log('[LOGIN] No existing session, proceeding with login');
      }

      console.log('[LOGIN] Calling signIn...');
      const result = await signIn({ 
        username: email.trim(), 
        password: password 
      });
      console.log('[LOGIN] SignIn successful:', result);
      
      // Navigation will be handled by the useEffect above or Amplify's state change
      navigate('/dashboard');
    } catch (err: any) {
      console.error('[LOGIN] Login error:', err);
      console.error('[LOGIN] Error name:', err.name);
      console.error('[LOGIN] Error message:', err.message);
      console.error('[LOGIN] Error stack:', err.stack);
      
      // Handle UserAlreadyAuthenticatedException gracefully
      if (err.name === 'UserAlreadyAuthenticatedException' || err.message?.includes('already a signed in user')) {
        console.log('[LOGIN] User already authenticated, redirecting');
        // User is already authenticated, just redirect
        navigate('/dashboard');
        return;
      }
      
      setError(err.message || 'Failed to sign in. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  // Show loading while checking authentication
  if (checkingAuth) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: '#0a0e27',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress sx={{ color: '#00D4FF' }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#0a0e27',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 50% 50%, rgba(0, 212, 255, 0.15) 0%, rgba(10, 14, 39, 0) 50%)',
          zIndex: 0,
        }
      }}
    >
      {/* Reusable Magic Background with Login Theme Colors */}
      <MagicBackground color="#00D4FF" blobColors={['rgba(0, 102, 255, 0.2)', 'rgba(0, 212, 255, 0.15)']} />

      <Container maxWidth="xs" sx={{ position: 'relative', zIndex: 1 }}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ 
            duration: 0.6, 
            ease: [0.22, 1, 0.36, 1] 
          }}
        >
          <Box sx={{ mb: 4, textAlign: 'center' }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <Logo size="large" />
            </Box>
            <Typography variant="h4" fontWeight={700} color="white" mb={1}>
              Welcome Back
            </Typography>
            <Typography variant="body1" color="rgba(255, 255, 255, 0.6)">
              Sign in to manage your AI agent
            </Typography>
          </Box>

          <Paper
            elevation={0}
            component="form"
            onSubmit={handleLogin}
            sx={{
              p: 4,
              borderRadius: 4,
              bgcolor: 'rgba(255, 255, 255, 0.03)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              backdropFilter: 'blur(20px)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
              transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
              '&:hover': {
                boxShadow: '0 12px 48px rgba(0, 0, 0, 0.3)',
              }
            }}
          >
            {error && (
              <Alert 
                severity="error" 
                sx={{ 
                  mb: 3, 
                  bgcolor: 'rgba(255, 107, 107, 0.1)', 
                  color: '#FF6B6B', 
                  border: '1px solid rgba(255, 107, 107, 0.2)',
                  '& .MuiAlert-icon': { color: '#FF6B6B' }
                }}
              >
                {error}
              </Alert>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <TextField
                fullWidth
                label="Email Address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email sx={{ color: 'rgba(255, 255, 255, 0.5)' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: 'white',
                    bgcolor: 'rgba(0, 0, 0, 0.2)',
                    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.1)' },
                    '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                    '&.Mui-focused fieldset': { borderColor: '#00D4FF' },
                  },
                  '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.5)' },
                  '& .MuiInputLabel-root.Mui-focused': { color: '#00D4FF' },
                }}
              />

              <TextField
                fullWidth
                label="Password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock sx={{ color: 'rgba(255, 255, 255, 0.5)' }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        sx={{ color: 'rgba(255, 255, 255, 0.5)' }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: 'white',
                    bgcolor: 'rgba(0, 0, 0, 0.2)',
                    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.1)' },
                    '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                    '&.Mui-focused fieldset': { borderColor: '#00D4FF' },
                  },
                  '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.5)' },
                  '& .MuiInputLabel-root.Mui-focused': { color: '#00D4FF' },
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                sx={{
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontSize: 16,
                  fontWeight: 700,
                  background: 'linear-gradient(135deg, #0066FF 0%, #00D4FF 100%)',
                  boxShadow: '0 8px 20px rgba(0, 102, 255, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #0052CC 0%, #00B8E6 100%)',
                    boxShadow: '0 12px 24px rgba(0, 102, 255, 0.4)',
                  },
                  '&.Mui-disabled': {
                    background: 'rgba(255, 255, 255, 0.1)',
                    color: 'rgba(255, 255, 255, 0.3)',
                  }
                }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Sign In'}
              </Button>
            </Box>

            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="rgba(255, 255, 255, 0.6)">
                Don't have an account?{' '}
                <Link
                  component="button"
                  type="button"
                  onClick={() => navigate('/signup')}
                  sx={{ color: '#00D4FF', textDecoration: 'none', fontWeight: 600 }}
                >
                  Start Free Trial
                </Link>
              </Typography>
            </Box>
          </Paper>
        </motion.div>
      </Container>
    </Box>
  );
}
